import type { IntegrationDraft } from '@/api/adminClient';
import { IntegrationVisibilityControl } from '@/components/integrations/IntegrationVisibilityControl';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { visibilitySummary } from '@/lib/integrationVisibility';

const READ_ONLY_TEXT: Record<'everyone' | 'assigned' | 'organization' | 'converting', string> = {
  everyone: 'Every organization can find and use it. It is on for them unless they turn it off.',
  assigned: 'Only the organizations a Studio admin assigns it to can find and use it. It is on for them unless they turn it off.',
  organization: 'The organization that built it is the only one that can use it.',
  converting: 'Studio is moving this integration to organization access. It takes a few minutes.',
};

/**
 * Who can use the connector: a Studio admin changes it here; an integration builder without Admin Access
 * sees it. A connector made from a Studio app keeps its own audience, so it shows nothing.
 */
export function WhoCanUsePanel({ draft }: { draft: Pick<IntegrationDraft, 'integrationId' | 'visibility' | 'ownerOrganizationId' | 'testingOrganizationIds' | 'appSource'> }) {
  const { isStudioAdmin } = useAdminAccess();
  if (draft.appSource) return null;
  if (isStudioAdmin) return <IntegrationVisibilityControl integrationId={draft.integrationId} />;
  const visibility = draft.visibility ?? null;
  const summary = visibilitySummary({ visibility, ownerOrganizationId: draft.ownerOrganizationId });
  const testing = draft.testingOrganizationIds?.length ?? 0;
  return (
    <AdminSection title="Who can use this" actions={<StatusPill tone={summary.tone}>{summary.label}</StatusPill>}>
      <p className="text-sm text-muted-foreground">{READ_ONLY_TEXT[visibility ?? 'converting']}</p>
      <p className="mt-2 text-xs text-muted-foreground">
        {testing === 0
          ? 'No testing organizations yet: publishing for testing uses your organization.'
          : `${testing === 1 ? '1 testing organization gets' : `${testing} testing organizations get`} the version published for testing.`}{' '}
        A Studio admin changes who can use it and who tests it.
      </p>
    </AdminSection>
  );
}
